import React, { useEffect, useRef, useState } from 'react';
import PageWrapper from './PageWrapper';
import Page from './Page';
import { RouteComponentProps } from 'react-router-dom';
import { MenuBar } from '@go-client/components';
import { BrandLogo } from '@go-client/components/UIkit/Brand/BrandLogo/BrandLogo';
import { useServerInfo } from '@go-client/context/ServerInfoContext';

import './BrandConfig.scss';
import BrandForm from '@go-client/components/BasicForms/BrandForm';
import PropertiesNavigation from '@go-client/components/Navigations/PropertiesNavigation';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import BrandCommonTextsForm from '@go-client/components/BasicForms/BrandCommonTextsForm';
import BrandDateTimeForm from '@go-client/components/BasicForms/BrandDateTimeForm';
import BrandEventLibraryForm from '@go-client/components/BasicForms/BrandEventLibraryForm';
import BrandVenueLibraryForm from '@go-client/components/BasicForms/BrandVenueLibraryForm';
import BrandContentLibraryForm from '@go-client/components/BasicForms/BrandContentLibraryForm';

export type BrandConfigPageProps = RouteComponentProps<{}>;

interface BrandConfigCategory {
  key: string;
  name: string;
}

export default function BrandConfig(props: BrandConfigPageProps) {
  const serverInfo = useServerInfo();

  const { brand, refreshContext: refreshActiveBrand } = serverInfo;
  const actionsRef = useRef<HTMLDivElement>(null);
  const [actionsElem, setActionsElem] = useState<HTMLDivElement | null>(null);

  useEffect(() => {
    setActionsElem(actionsRef.current);
  }, []);

  const categories: BrandConfigCategory[] = [];

  categories.push(
    {
      key: 'brandManage',
      name: 'Brand Details',
    },
    {
      key: 'dateTime',
      name: 'Date & Time',
    },
    {
      key: 'commonTexts',
      name: 'Common Texts',
    },
    {
      key: 'eventLibrary',
      name: 'Event Library',
    },
    {
      key: 'venueLibrary',
      name: 'Venue Library',
    },
    {
      key: 'contentLibrary',
      name: 'Content Library',
    }
  );

  const [activeCategory, setActiveCategory] = useState<BrandConfigCategory | undefined>(
    categories[0]
  );

  return (
    <PageWrapper {...props}>
      <Page className="BrandConfig absolute flex items-stretch">
        <MenuBar {...props} />
        <div className="BrandConfigContent bg-blue-lightest overflow-y-auto flex-grow">
          <div
            className="BrandConfigHeader align-with-logo flex items-end sticky top-0
            left-0 z-10 bg-white shadow"
          >
            <BrandLogo logo={brand.logo} />
            <div className="mb-3 px-5 pl-8 flex-grow flex items-end">
              <PropertiesNavigation active="brand-config" />
            </div>
          </div>
          <div className="BrandConfigBody content-minus-logo">
            <div style={{ maxWidth: '1750px' }} className="mx-auto">
              <div
                className="sticky w-full left-0 bg-blue-lightest"
                style={{ top: '130px', height: '2rem', zIndex: 2 }}
              />
              <div className="px-10">
                <div className="grid grid-cols-4 gap-4 items-start">
                  <div className="bg-white shadow-md rounded overflow-clip">
                    {categories.map(c => (
                      <div
                        key={c.key}
                        className={`list-item px-5 py-3 cursor-pointer ${
                          activeCategory && activeCategory.key === c.key ? 'active' : ''
                        }`}
                        onClick={() => setActiveCategory(c)}
                      >
                        <div className="flex items-center">
                          <div className="flex-grow min-w-0">
                            <div className="list-item-name font-semibold overflow-ellipsis">
                              {c.name}
                            </div>
                          </div>
                          <FontAwesomeIcon
                            icon="chevron-right"
                            className="list-item-arrow ml-auto pl-3 flex-shrink-0"
                          />
                        </div>
                      </div>
                    ))}
                    {categories.length < 1 && (
                      <div className="grid items-center justify-center italic text-smoke bg-eggshell px-3 py-5">
                        No configuratible options found
                      </div>
                    )}
                  </div>
                  {activeCategory && (
                    <div
                      className="sticky col-span-3"
                      style={{
                        top: 'calc(130px + 2rem)',
                        height: 'calc(100vh - 130px - 4rem)',
                      }}
                    >
                      <div className="bg-white shadow-md max-h-full flex flex-col rounded overflow-clip">
                        <div className="flex-grow overflow-auto">
                          {activeCategory.key === 'brandManage' && actionsElem && (
                            <div className="p-5 grid grid-cols-1 gap-4">
                              <BrandForm
                                edit={brand}
                                actionsElem={actionsElem}
                                onFormSubmitted={refreshActiveBrand}
                              />
                            </div>
                          )}
                          {activeCategory.key === 'dateTime' && actionsElem && (
                            <div className="p-5 grid grid-cols-1 gap-4">
                              <BrandDateTimeForm
                                edit={brand}
                                actionsElem={actionsElem}
                                onFormSubmitted={refreshActiveBrand}
                              />
                            </div>
                          )}
                          {activeCategory.key === 'commonTexts' && actionsElem && (
                            <div className="p-5 grid grid-cols-1 gap-4">
                              <BrandCommonTextsForm
                                edit={brand}
                                actionsElem={actionsElem}
                                onFormSubmitted={refreshActiveBrand}
                              />
                            </div>
                          )}
                          {activeCategory.key === 'eventLibrary' && actionsElem && (
                            <div className="p-5 grid grid-cols-1 gap-4">
                              <BrandEventLibraryForm
                                edit={brand}
                                actionsElem={actionsElem}
                                onFormSubmitted={refreshActiveBrand}
                              />
                            </div>
                          )}
                          {activeCategory.key === 'venueLibrary' && actionsElem && (
                            <div className="p-5 grid grid-cols-1 gap-4">
                              <BrandVenueLibraryForm
                                edit={brand}
                                actionsElem={actionsElem}
                                onFormSubmitted={refreshActiveBrand}
                              />
                            </div>
                          )}
                          {activeCategory.key === 'contentLibrary' && actionsElem && (
                            <div className="p-5 grid grid-cols-1 gap-4">
                              <BrandContentLibraryForm
                                edit={brand}
                                actionsElem={actionsElem}
                                onFormSubmitted={refreshActiveBrand}
                              />
                            </div>
                          )}
                          <div
                            className="sticky bottom-0 p-3 bg-eggshell border-t"
                            style={{ zIndex: 1001 }}
                            ref={actionsRef}
                          />
                        </div>
                      </div>
                    </div>
                  )}
                </div>
                <div className="mt-3">&nbsp;</div>
              </div>
            </div>
          </div>
        </div>
      </Page>
    </PageWrapper>
  );
}
